import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { useCreatePatientReport } from "@/services/query/reports.query"
import { useToast } from "@/components/ui/use-toast"

const reportFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  details: z.string().min(1, "Details are required"),
  media_file: z.any(),
})

export function AddReportDialog({ open, onOpenChange, patientId }) {
  const { toast } = useToast()
  const createReportMutation = useCreatePatientReport(patientId)
  const [file, setFile] = useState(null)

  const form = useForm({
    resolver: zodResolver(reportFormSchema),
    defaultValues: {
      title: "",
      details: "",
      media_file: undefined,
    },
  })

  const onSubmit = async (values) => {
    try {
      const formData = new FormData()
      formData.append("title", values.title)
      formData.append("details", values.details)
      if (file) {
        formData.append("media_file", file)
      }

      await createReportMutation.mutateAsync(formData)
      toast({ title: "Report created successfully" })
      onOpenChange(false)
      form.reset()
      setFile(null)
    } catch (error) {
      toast({
        title: "Error creating report",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Report</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Report title" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="details"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Details</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter detailed report information..."
                      className="min-h-[120px] resize-vertical"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="media_file"
              render={() => (
                <FormItem>
                  <FormLabel>Attachment</FormLabel>
                  <FormControl>
                    <Input
                      type="file"
                      onChange={(e) => setFile(e.target.files?.[0])}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={createReportMutation.isLoading}>
                {createReportMutation.isLoading ? "Adding..." : "Add Report"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default AddReportDialog
